import React, { useState } from 'react';
import { Button, Modal, ModalTitle, ModalContent, ModalActions } from '@c2fo/liquidity';
import { EditUserModal } from './EditUserModal';
import { ResetPasswordModal } from './ResetPasswordModal';
import type { User } from '../types';

interface ActionButtonsProps {
  user: User | null;
  canImpersonate: boolean;
  impersonating: boolean;
  editing: boolean;
  sendingPasswordReset: boolean;
  resettingPassword: boolean;
  forgettingUser: boolean;
  onImpersonate: () => void;
  onEditUser: (userData: Partial<User>) => Promise<void>;
  onSendPasswordResetEmail: () => Promise<void>;
  onResetPassword: (password: string) => Promise<void>;
  onForgetUser: () => Promise<void>;
}

export const ActionButtons: React.FC<ActionButtonsProps> = ({
  user,
  canImpersonate,
  impersonating,
  editing,
  sendingPasswordReset,
  resettingPassword,
  forgettingUser,
  onImpersonate,
  onEditUser,
  onSendPasswordResetEmail,
  onResetPassword,
  onForgetUser,
}) => {
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPasswordResetConfirmModal, setShowPasswordResetConfirmModal] = useState(false);

  const isUserForgotten = !!user?.forgottenOn;

  return (
    <>
      <div className="flex justify-between items-center mt-6 pt-6 border-t border-gray-300">
        <div className="flex gap-2">
          <Button
            variant="primary"
            size="sm"
            onClick={() => setShowEditModal(true)}
            disabled={editing || isUserForgotten}
            loading={editing}
          >
            Edit User
          </Button>
        </div>

        <div className="flex gap-2">
          {canImpersonate && (
            <Button
              variant="primary"
              size="sm"
              onClick={onImpersonate}
              loading={impersonating}
              disabled={impersonating || isUserForgotten}
            >
              {impersonating ? 'Impersonating...' : 'Impersonate User'}
            </Button>
          )}
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setShowPasswordResetConfirmModal(true)}
            disabled={sendingPasswordReset || isUserForgotten}
            loading={sendingPasswordReset}
          >
            Send Password Reset Email
          </Button>
          <Button
            variant="tertiary"
            size="sm"
            onClick={() => setShowResetPasswordModal(true)}
            disabled={resettingPassword}
          >
            Reset Password
          </Button>
        </div>
      </div>

      <ResetPasswordModal
        open={showResetPasswordModal}
        onClose={() => setShowResetPasswordModal(false)}
        onReset={onResetPassword}
        userEmail={user?.emailAddress || ''}
        isLoading={resettingPassword}
      />

      <Modal open={showPasswordResetConfirmModal} onClose={() => setShowPasswordResetConfirmModal(false)} size="sm">
        <ModalTitle>Confirm Password Reset Email</ModalTitle>
        <ModalContent>
          <div className="space-y-2">
            <p>You are about to send a password reset email to:</p>
            <p className="font-semibold">{user?.emailAddress}</p>
            <p>Are you sure you want to continue?</p>
          </div>
        </ModalContent>
        <ModalActions>
          <Button type="button" variant="secondary" onClick={() => setShowPasswordResetConfirmModal(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            loading={sendingPasswordReset}
            onClick={async () => {
              setShowPasswordResetConfirmModal(false);
              await onSendPasswordResetEmail();
            }}
          >
            Send Email
          </Button>
        </ModalActions>
      </Modal>

      <EditUserModal
        open={showEditModal}
        user={user}
        onClose={() => setShowEditModal(false)}
        onSave={onEditUser}
        onForgetUser={onForgetUser}
        saving={editing}
        forgettingUser={forgettingUser}
      />
    </>
  );
};
